import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC } from 'react';
import { Button, RHFTextField } from '@higo/ui';
import { FormattedMessage } from 'react-intl';
import { object, ref, string } from 'yup';
import { passwordValidationRegex } from 'config/validationSchemaParams';
import { setYupDefaultMessages } from 'config/yupLocale';

setYupDefaultMessages();
const schema = object({
  password: string()
    .required()
    .matches(passwordValidationRegex, 'validation.passwordRegex'),

  passwordConfirmation: string()
    .required()
    .oneOf([ref('password')], 'validation.passwordMatch'),
});

export interface SetNewPasswordFormModel {
  password: string;
  passwordConfirmation: string;
}

const defaultValues: SetNewPasswordFormModel = {
  password: '',
  passwordConfirmation: '',
};

export type SetNewPasswordFormProps = {
  onSubmit: (value: SetNewPasswordFormModel) => Promise<unknown>;
  passwordServerError?: string | null;
  onPasswordServerErrorClear?: () => void;
};

export const SetNewPasswordForm: FC<SetNewPasswordFormProps> = ({
  onSubmit,
  passwordServerError,
  onPasswordServerErrorClear,
}) => {
  const {
    handleSubmit,
    control,
    clearErrors,
    setError,
    watch,
    formState: { isSubmitting },
  } = useForm<SetNewPasswordFormModel>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const passwordValue = watch('password');
  const previousPasswordValueRef = useRef(passwordValue);

  useEffect(() => {
    if (!passwordServerError) {
      return;
    }

    setError('password', {
      type: 'server',
      message: passwordServerError,
    });
  }, [passwordServerError, setError]);

  useEffect(() => {
    if (
      passwordServerError &&
      previousPasswordValueRef.current !== passwordValue
    ) {
      clearErrors('password');
      onPasswordServerErrorClear?.();
    }

    previousPasswordValueRef.current = passwordValue;
  }, [clearErrors, onPasswordServerErrorClear, passwordServerError, passwordValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RHFTextField
        control={control}
        type="password"
        label="common.password"
        name="password"
        autoComplete="new-password"
      />
      <RHFTextField
        control={control}
        type="password"
        label="common.passwordConfirmation"
        name="passwordConfirmation"
        autoComplete="new-password"
      />
      <Button
        type="submit"
        disabled={isSubmitting}
        isLoading={isSubmitting}
        fullWidth
      >
        <FormattedMessage id="authentication.setNewPassword.saveNewPassword" />
      </Button>
    </form>
  );
};
